import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCcw } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
  requireAdmin?: boolean;
}

export function ProtectedRoute({ 
  children, 
  redirectTo = '/login',
  requireAdmin = false
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, refreshSession } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSessionRefresh = async () => {
    setIsRefreshing(true);
    setAuthError(null);
    try {
      await refreshSession();
      // Wait a moment to check if auth state updated
      setTimeout(() => {
        if (!isAuthenticated) {
          setAuthError("Could not refresh your session. Please log in again.");
        }
      }, 1000);
    } catch (error) {
      setAuthError("Failed to refresh session. Please try logging in again.");
      console.error("Session refresh error:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // Only redirect after loading is complete and we know user is not authenticated
    if (!isLoading) {
      if (!isAuthenticated) {
        // Save current location to redirect back after login
        navigate(redirectTo, { 
          replace: true, 
          state: { from: location.pathname } 
        });
      } else if (requireAdmin) {
        // Check admin status if needed (this is just a placeholder - implement actual admin check)
        // You would implement admin check logic here
      }
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo, location.pathname, requireAdmin]);

  if (isLoading || isRefreshing) {
    return (
      <div className="flex flex-col h-screen w-full items-center justify-center gap-4">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600">{isRefreshing ? 'Refreshing your session...' : 'Verifying your credentials...'}</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex flex-col h-screen w-full items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Authentication Error</AlertTitle>
            <AlertDescription>{authError}</AlertDescription>
          </Alert>
          
          <div className="flex flex-col space-y-2">
            <Button 
              onClick={handleSessionRefresh} 
              disabled={isRefreshing}
              className="w-full"
            >
              {isRefreshing ? (
                <>
                  <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Try Again
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => navigate('/login')} 
              className="w-full"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
